import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import resourcesAPI from 'api/resources';
import { RejectWithMessage, TableDataResponse } from 'features/types';
import { Paging } from 'api/types';
import { EventsState, PublicJobBoardResourceTableView, GetPublicJobBoardResourcesTableFilters } from './types';
import { getErrorMessage } from 'utils/error';
import { RootState } from 'app/root-reducer';

export const getEventsTable = createAsyncThunk<TableDataResponse<PublicJobBoardResourceTableView>, void, RejectWithMessage>(
  'events/get-table',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState();
      return await resourcesAPI.getEventsTable(state.events.requestData);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

const initialState: EventsState = {
  data: {
    tableDataIsLoading: false,
    tableData: {
      rows: [],
      totalCount: 0,
      noResult: false,
    },
  },
  requestData: {
    paging: { page: 1, pageSize: 20 },
    filters: {},
  },
  status: 'idle',
  error: null,
};

const eventsSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    resetRequestData: (state) => {
      state.requestData = { ...initialState.requestData };
    },
    setPage: (state, action: PayloadAction<Paging>) => {
      state.requestData.paging.page = action.payload.page;
      state.requestData.paging.pageSize = action.payload.pageSize;
    },
    setFilters: (state, action: PayloadAction<GetPublicJobBoardResourcesTableFilters>) => {
      state.requestData.filters = { ...action.payload };
      state.requestData.paging = initialState.requestData.paging;
    },
    resetTableData: (state) => {
      state.data.tableData = initialState.data.tableData;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getEventsTable.pending, (state) => {
      state.data.tableDataIsLoading = true;
    });
    builder.addCase(getEventsTable.fulfilled, (state, action: PayloadAction<TableDataResponse<PublicJobBoardResourceTableView>>) => {
      state.data.tableDataIsLoading = false;
      state.data.tableData = action.payload;
      state.data.tableData.noResult = state.data.tableData.rows.length === 0;
    });
    builder.addCase(getEventsTable.rejected, (state) => {
      state.data.tableDataIsLoading = false;
    });
  },
});

export const { resetRequestData, setPage, setFilters, resetTableData } = eventsSlice.actions;
export const eventsReducer = eventsSlice.reducer;
